import { friendly } from './ui/copy';
// The match HUD (2026-10-25, ui branch; the graphics pass 2026-10-26, ui2). DOM over the canvas, kept to the edges so
// the middle of the battlefield stays clear: top left the pause button, the clock plaque (the phase line and a bar
// for the time left in it) and the minimap (ui/minimap.ts); top right the four players (army portrait, soldiers, base
// health); along the bottom your tier button (a coin with the next tier and a bar of gold toward it), your hero (portrait
// with its level coin, health and mana, XP as the plaque's fill line), its two skills (icon, hotkey, mana cost, a
// cooldown sweep), the center's shop, your spawn slots (unit portraits; the line under each fills toward its next
// soldier) and your goal. A tap on a slot, the shop or Attack opens a sheet over the bar, never over the middle.
// Toasts are a navy herald banner under the top edge. The nodes are built once per match and only their text, widths
// and custom properties change each frame, so a tap is never lost to a redraw. The bottom row is rebuilt only when your
// tier, slots or hero change. When the bar would run past the window the hero lifts to sit above the tier button
// (.lifted); on a portrait phone the bar is three short rows (hero, slots, tier and goals) and the slot row folds away
// behind a tab (.folded), so the battlefield keeps two thirds of the screen. Symbols (gold, mana, the clock, the goals)
// are painted tin from symbols.webp (art.ts SYMBOLS); the hero's portrait sits in a gold medallion.
import { BASE, CLASS, ClassId, ECON, EXTRA, FACTION, HERO, ITEM, ITEMS, TIER_COST, TIER_GATE, unitStats } from '../sim/defs';
import type { Command, Ev, Goal, Sim } from '../sim/sim';
import { armyPic, heroPic, iconPic, setPic, unitPic, type Pic } from './ui/art';
import { CLASS_LABEL, CLASS_ROLE, TEAM, TEAM_NAME, giantView, heroView, mmss, phaseLine, skillView, slotChoices, who } from './ui/data';
import { Minimap, type Ground } from './ui/minimap';

type Hooks = {
  send: (c: Command) => boolean; pickArmy: () => void; pause: () => void;
  /** The camera's footprint on the table (four x, z corners) and a move of the camera, for the minimap. */
  view: () => number[]; jump: (x: number, z: number) => void;
  /** The minimap's ground colors for the season and the snow lying. */
  ground?: () => Ground;
  /** Unit control (control/controls.ts): a goal button orders the selection when there is one (true when it did), and
   *  the goal line names the selection instead of the army's goal. */
  goal?: (g: Goal) => boolean; target?: () => string | null;
};
type Tone = 'info' | 'good' | 'warn';

const h = <K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = '') => {
  const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e;
};
/** Write only what changed: the HUD updates every frame and most frames change nothing. */
const put = (e: HTMLElement, text: string) => { if (e.textContent !== text) e.textContent = text; };
const width = (e: HTMLElement, share: number) => { const v = `scaleX(${Math.max(0, Math.min(1, share)).toFixed(3)})`; if (e.style.transform !== v) e.style.transform = v; };
const prop = (e: HTMLElement, k: string, v: string) => { if (e.style.getPropertyValue(k) !== v) e.style.setProperty(k, v); };
const flag = (e: HTMLElement, k: string, v: string) => { if (e.dataset[k] !== v) e.dataset[k] = v; };
const pic = (p: Pic, cls = '') => { const e = h('i', 'pic' + (cls ? ' ' + cls : '')); e.setAttribute('aria-hidden', 'true'); setPic(e, p); return e; };
const svg = (path: string, size = 22) => `<svg viewBox="0 0 24 24" width="${size}" height="${size}" aria-hidden="true">${path}</svg>`;
const LOCK_ICON = svg('<path d="M7 10V7.5a5 5 0 0 1 10 0V10h1.5v11h-13V10zm2.5 0h5V7.5a2.5 2.5 0 0 0-5 0z" fill="currentColor"/>', 12);
const sym = (name: string, label = '') => { const e = h('i', 'sym'); e.dataset.sym = name; if (label) { e.setAttribute('role', 'img'); e.setAttribute('aria-label', label); } else e.setAttribute('aria-hidden', 'true'); return e; };
const FOLD_ICON = svg('<path d="M6 9.5l6 6 6-6" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>', 18);
const PAUSE_ICON = svg('<rect x="6" y="5" width="4" height="14" rx="1.5" fill="currentColor"/><rect x="14" y="5" width="4" height="14" rx="1.5" fill="currentColor"/>');

type SkillBtn = { b: HTMLButtonElement; sweep: HTMLElement; secs: HTMLElement; cost: HTMLElement; costV: HTMLElement; state: string };
type HeroBox = {
  box: HTMLElement; face: HTMLElement; lv: HTMLElement; hp: HTMLElement; mana: HTMLElement; xp: HTMLElement; back: HTMLElement; bars: HTMLElement;
  skills: SkillBtn[]; shop: HTMLButtonElement; shopText: HTMLElement;
};

/** A live salve timer uses the hero's existing shop label, including while away from the shop. */
export function salveWords(s: Sim, i: number) { return i >= 0 && s.salveT[i] > 0 ? `Heal ${Math.ceil(s.salveT[i])}s` : 'Shop'; }

export function buyRefusal(s: Sim, p: number, item: string) {
  const h = s.players[p].hero, i = h?.i ?? -1;
  if (i < 0 || !s.nearCenter(i)) return 'Bring your hero to the center';
  if (item === 'salve' && s.hp[i] >= s.maxHp[i]) return 'Health is full';
  if (item === 'salve' && s.salveT[i] > 0) return 'Already healing';
  if (item === 'whetstone' && h!.whet >= ITEM.whetstone.max) return 'All 5 bought';
  return 'Not enough gold';
}

export function goalWords(g: Goal, me: number) {
  return g.kind === 'center' ? 'Holding center' : g.kind === 'point' ? 'Rallying at flag' : g.p === me ? 'Defending home' : `Attacking ${TEAM_NAME[g.p]}`;
}

export class Hud {
  readonly el = h('div', 'hud');
  readonly minimap: Minimap;
  private me = -1;
  private sim!: Sim;
  private clock = h('b', 'clock-t');
  private phase = h('span', 'phase');
  private phaseFill = h('i');
  private board = h('div', 'board');
  private rows: { el: HTMLElement; bust: HTMLElement; n: HTMLElement; nv: HTMLElement; hp: HTMLElement; faction: string | null; out: boolean }[] = [];
  private bar = h('div', 'bar');
  /** Where control-group buttons go (the controls branch, 2026-10-26: 3 to 5 small `.group` buttons): kept across
   *  matches, empty and hidden until something fills it. index.html places it just above the bar's right end, near the
   *  skills, and on a portrait phone as a rail up the right edge so the battlefield keeps its rows; the UI walk fills it
   *  with five stand-ins at every size and checks it fits (uishots 5g_groups). */
  readonly groups = Object.assign(h('div', 'groups'), { role: 'group', ariaLabel: 'Control groups' });
  private toast = h('div', 'toast');
  private sheet = h('div', 'sheet');
  private toastUntil = 0;
  private sig = '';
  private live: { tier?: HTMLButtonElement; tierT?: HTMLElement; tierN?: HTMLElement; tierS?: HTMLElement; tierFill?: HTMLElement; fills: HTMLElement[]; slotBtns: HTMLButtonElement[]; goal?: HTMLElement; goalBtns: HTMLButtonElement[]; hero?: HeroBox } = { fills: [], slotBtns: [], goalBtns: [] };
  /** The portrait phone's slot row folded away, once the player has tapped the tab (kept across matches this visit).
   *  Untapped, it starts folded (2026-10-30): the battlefield gets 71% of a 390 x 844 screen instead of 66%, and the
   *  Slots tab opens the row (out/clarity HUD mock, Take A). See folded() in the bar's build for the exceptions. */
  private folded: boolean | null = null;
  /** Room for the hero row beside the Slots tab; a phone under 360 px keeps the row open until tapped. */
  private roomy = matchMedia('(min-width: 375px) and (min-height: 360px)');
  private applyFold = () => {};
  private attackedSaid = -1e9;
  /** The giant's wave already warned about (ten seconds ahead). */
  private giantSaid = 0;
  private sheetFor: 'none' | 'attack' | 'shop' | number = 'none';
  /** The open slot sheet's choices, kept so its lock countdown and buttons follow the clock (Astra F4). */
  private slotSheet: { i: number; note: HTMLElement; btns: { b: HTMLButtonElement; full: boolean }[] } | null = null;
  /** The Attack sheet's Extra button, kept live while the sheet is open (Astra 2026-10-27 F2: it froze at its opening state). */
  private extraBtn: { b: HTMLButtonElement; unit: HTMLElement; name: string } | null = null;

  constructor(readonly root: HTMLElement, readonly hooks: Hooks) {
    this.roomy.addEventListener('change', () => this.applyFold());
    const tl = h('div', 'tl'), top = h('div', 'tl-row');
    const pause = h('button', 'icon pause plaque'); pause.innerHTML = PAUSE_ICON; pause.setAttribute('aria-label', 'Pause');
    pause.addEventListener('click', () => hooks.pause());
    const clock = h('div', 'clock plaque'), pbar = h('span', 'phase-bar');
    pbar.append(this.phaseFill); clock.append(sym('timer'), this.clock, this.phase, pbar);
    clock.setAttribute('role', 'timer'); clock.setAttribute('aria-live', 'off');
    top.append(pause, clock);
    this.minimap = new Minimap({ view: hooks.view, jump: hooks.jump, ground: hooks.ground });
    tl.append(top, this.minimap.el);
    this.toast.setAttribute('role', 'status');
    this.sheet.hidden = true;
    this.el.append(tl, this.board, this.toast, this.bar, this.sheet);
    root.append(this.el);
    addEventListener('resize', () => { if (this.me >= 0) this.fitBar(); });
  }

  /** A new match: you are seat `me` (-1 when watching). */
  setMatch(me: number, sim: Sim) {
    this.me = me; this.sim = sim; this.sig = ''; this.toastUntil = 0; this.giantSaid = 0; this.toast.classList.remove('on'); this.closeSheet();
    this.el.classList.toggle('watch', me < 0);
    this.minimap.setSim(sim, me);
    this.board.replaceChildren(); this.rows = [];
    sim.players.forEach((_, p) => {
      const el = h('div', 'pl' + (p === me ? ' me' : '')); el.style.setProperty('--team', TEAM[p]);
      const bust = pic({ strip: 'none', frame: 0 }, 'bust'), nm = h('b', 'nm', who(p, me)), n = h('span', 'n'), nv = h('span'), hp = h('span', 'hp'), fill = h('i');
      n.setAttribute('title', 'Soldiers on the field'); hp.setAttribute('title', 'Barracks health'); hp.append(fill); n.append(sym('attack'), nv);
      el.append(bust, nm, n, hp); this.board.append(el);
      this.rows.push({ el, bust, n, nv, hp: fill, faction: '', out: false });
    });
    this.bar.replaceChildren();
    this.bar.hidden = me < 0;
  }
  show(on: boolean) { this.el.hidden = !on; if (!on) this.closeSheet(); }

  say(text: string, secs = 3, tone: Tone = 'info') {
    const t = this.toast;
    flag(t, 'tone', tone);
    if (t.textContent !== text || !t.classList.contains('on')) { put(t, text); t.classList.remove('on'); void t.offsetWidth; t.classList.add('on'); }
    this.toastUntil = performance.now() + secs * 1000;
  }
  flashGoal(g: Goal) {
    this.say(g.kind === 'center' ? 'Hold the center' : g.kind === 'base' ? (g.p === this.me ? 'Fall back to defend' : `March on ${TEAM_NAME[g.p]}`) : 'Rally at the flag', 2);
  }

  onEvent(e: Ev) {
    const me = this.me;
    if (e.e === 'tier' && e.p === me) this.say(e.tier === 1 ? `You lead the ${FACTION[e.faction!].name}` : `Tier ${e.tier}: ${this.unlocks(e.tier)} unlocked`, 4, 'good');
    if (e.e === 'phase') this.say(e.name === 'bases-open' ? 'Attack bases now' : 'Bases crumble. The center hunts the leader.', 5, 'warn');
    if (e.e === 'out' && e.p !== me) this.say(`${TEAM_NAME[e.p]} is out`, 4);
    // the center's giant: it wakes (with the bases opening, the first time), grows, falls to someone
    if (e.e === 'boss') this.say(e.grew ? 'Drum Giant grows stronger'
      : Math.abs(this.sim.time - BASE.safeUntil) < 1 ? 'Attack bases. Fight the Drum Giant at center.' : 'Fight the Drum Giant at center', 5, 'warn');
    if (e.e === 'bossDown') this.say(e.p === me ? `Giant defeated: +${e.gold} gold. Your army hits harder.` : e.p >= 0 ? `${TEAM_NAME[e.p]} slew the Drum Giant` : 'The Drum Giant fell', 5, e.p === me ? 'good' : 'info');
    const myHero = me >= 0 ? this.sim.players[me].hero : null, dh = myHero ? HERO[myHero.id] : null;
    if (e.e === 'level' && e.p === me && dh) {
      const opens = dh.skills.find((k) => k.level === e.level);
      this.say(`${dh.short}: level ${e.level}${opens ? `. Use ${opens.name}` : ''}`, 4, 'good');
      const lv = this.live.hero?.box; if (lv) { lv.classList.remove('lvup'); void lv.offsetWidth; lv.classList.add('lvup'); }
    }
    if (e.e === 'heroDown' && e.p === me && dh) this.say(`${dh.short} fell. Back in ${Math.ceil(myHero!.respawn - this.sim.time)} s`, 4, 'warn');
    else if (e.e === 'heroDown' && e.by === me) { const v = this.sim.players[e.p].hero; if (v) this.say(`You brought down ${TEAM_NAME[e.p]}'s ${HERO[v.id].short}`, 3, 'good'); }
    if (e.e === 'hero' && e.p === me && dh && myHero!.deaths > 0) this.say(`${dh.short} is back`, 2);
    if (e.e === 'baseHit' && e.p === me && performance.now() - this.attackedSaid > 12_000) {
      this.attackedSaid = performance.now(); this.say('Defend your base', 3, 'warn');
      const r = this.rows[me]?.el;
      if (r) { r.classList.remove('hit'); void r.offsetWidth; r.classList.add('hit'); }
    }
  }
  private unlocks(t: number) { return slotChoices(t).filter((c) => CLASS[c].tier === t).map((c) => CLASS_LABEL[c]).join(' and '); }

  update() {
    const s = this.sim;
    if (this.toastUntil && performance.now() > this.toastUntil) { this.toastUntil = 0; this.toast.classList.remove('on'); }
    put(this.clock, mmss(s.time));
    const gv = giantView(s), ph = phaseLine(s.time, gv);
    if (!gv.up && gv.next - s.time <= 10 && gv.next > s.time && this.giantSaid !== gv.next) { this.giantSaid = gv.next; this.say('Drum Giant wakes in 10 s', 3, 'warn'); }
    put(this.phase, ph.text);
    width(this.phaseFill, ph.left);
    // while the Drum Giant stands the plaque is its health bar: its drum for the hourglass, the fill in its red
    const plaque = this.phase.parentElement!, giant = gv.up && ph.text.includes('Giant');
    flag(plaque, 'tone', ph.tone); flag(plaque, 'giant', giant ? 'up' : '');
    flag(plaque.firstElementChild as HTMLElement, 'sym', giant ? 'giant' : 'timer');
    s.players.forEach((pl, p) => {
      const r = this.rows[p];
      const face = pl.faction ?? pl.hero?.id ?? null;
      if (r.faction !== face) {
        // the army's footman once picked; until then the hero the player leads (picked at 0:00), else the team's disc
        r.faction = face;
        setPic(r.bust, pl.faction ? armyPic(pl.faction) : pl.hero ? heroPic(pl.hero.id) : { strip: 'none', frame: 0 });
        r.el.setAttribute('aria-label', `${who(p, this.me)}, ${pl.faction ? FACTION[pl.faction].name : 'no army yet'}`);
      }
      if (r.out !== pl.out) { r.out = pl.out; r.el.classList.toggle('out', pl.out); }
      put(r.nv, pl.out ? 'Out' : String(pl.alive));
      width(r.hp, pl.baseHp / pl.baseMax);
    });
    this.minimap.update(performance.now());
    if (this.me >= 0) this.updateBar();
  }

  private updateBar() {
    const s = this.sim, me = s.players[this.me];
    if (me.out) { if (!this.bar.hidden) { this.bar.hidden = true; this.closeSheet(); } return; }
    const t = me.tier, sig = [t, me.slots.join(','), me.faction, me.hero?.id].join('|');
    if (sig !== this.sig) { this.sig = sig; this.buildBar(); }
    this.updateHero();
    const L = this.live, gate = TIER_GATE[t], cost = TIER_COST[t], gold = Math.floor(me.gold);
    const ready = s.canTier(this.me);
    put(L.tierT!, t >= 4 ? 'Top tier' : ready && t === 0 ? 'Pick army' : ready ? 'Tier up' : `Tier ${t + 1}`);
    put(L.tierN!, t >= 4 ? String(t) : String(t + 1));
    put(L.tierS!, t >= 4 ? `${gold}` : `${gold} / ${cost}`); // the whole purse: past the cost it is shop money (Astra F5)
    width(L.tierFill!, t >= 4 ? 1 : gold / cost);
    if (L.tier!.disabled === ready) L.tier!.disabled = !ready;
    L.tier!.classList.toggle('ready', ready);
    L.tier!.classList.toggle('gated', t < 4 && s.time < gate);
    const f = s.faction(this.me);
    me.slots.forEach((c, i) => {
      const iv = unitStats(c, t, f).interval;
      width(L.fills[i], 1 - Math.max(0, me.slotT[i]) / iv);
      L.slotBtns[i]?.classList.toggle('locked', me.slotLock[i] > s.time);
    });
    put(L.goal!, this.hooks.target?.() ?? goalWords(me.goal, this.me));
    const g = me.goal, on = g.kind === 'center' ? 0 : g.kind === 'base' ? (g.p === this.me ? 1 : 2) : -1;
    L.goalBtns.forEach((b, k) => { const v = String(k === on); if (b.getAttribute('aria-pressed') !== v) b.setAttribute('aria-pressed', v); });
    // the base you march on, as its name and color on the Attack button (the goal line is hidden on a portrait phone, and a
    // color alone fails a color-blind player: Astra, uiart review F1)
    const atk = L.goalBtns[2], foe = on === 2 && g.kind === 'base' ? g.p : -1, target = foe < 0 ? '' : TEAM[foe];
    flag(atk, 'target', target); prop(atk, '--team', target);
    put(atk.lastElementChild as HTMLElement, foe < 0 ? 'Attack' : TEAM_NAME[foe]);
    const said = foe < 0 ? 'Attack' : `Attack ${TEAM_NAME[foe]}`;
    if (atk.getAttribute('aria-label') !== said) atk.setAttribute('aria-label', said);
    if (this.extraBtn) this.updateExtra();
    const sh = this.slotSheet;
    if (sh) {
      const left = me.slotLock[sh.i] - s.time;
      put(sh.note, left > 0 ? `Change again in ${Math.ceil(left)} s.` : '');
      sh.note.hidden = left <= 0;
      for (const { b, full } of sh.btns) { const d = left > 0 || full; if (b.disabled !== d) b.disabled = d; }
    }
  }

  private updateHero() {
    const s = this.sim, v = this.live.hero, hv = heroView(s, this.me), hr = s.players[this.me].hero;
    if (!hv || !v || !hr) return;
    put(v.lv, String(hv.level));
    width(v.xp, hv.xp);
    flag(v.box, 'alive', String(hv.alive));
    if (hv.alive) { width(v.hp, hv.hp); width(v.mana, hv.mana); }
    else put(v.back, `Back in ${hv.back} s`);
    prop(v.face, '--sweep', hv.revive.toFixed(3));
    HERO[hr.id].skills.forEach((k, i) => {
      const sk = v.skills[i], sv = skillView(s, this.me, i as 0 | 1)!;
      if (sk.state !== sv.state) {
        // a skill coming off cooldown (or getting its mana) flashes once, so you see it is up without reading numbers
        if (sv.state === 'ready' && (sk.state === 'cooldown' || sk.state === 'short')) { sk.b.classList.remove('up'); void sk.b.offsetWidth; sk.b.classList.add('up'); }
        sk.state = sv.state; flag(sk.b, 'state', sv.state);
      }
      prop(sk.sweep, '--sweep', sv.sweep.toFixed(3));
      put(sk.secs, sv.state === 'cooldown' || (sv.state === 'down' && sv.secs > 0) ? String(sv.secs) : '');
      put(sk.costV, sv.state === 'locked' ? `Lv ${k.level}` : String(k.mana));
      const off = sv.state !== 'ready';
      if (sk.b.disabled !== off) sk.b.disabled = off;
      const label = `${k.name}. Key ${i ? 'E' : 'Q'}. ${sv.state === 'locked' ? `opens at level ${k.level}` : sv.state === 'down' ? 'your hero is down' : sv.state === 'stunned' ? 'your hero is stunned' : sv.state === 'cooldown' ? `ready in ${sv.secs} s` : sv.state === 'short' ? `needs ${k.mana} mana` : `${k.mana} mana`}. ${friendly(k.text)}`;
      if (sk.b.getAttribute('aria-label') !== label) sk.b.setAttribute('aria-label', label);
    });
    const open = hv.alive && s.nearCenter(hr.i);
    if (v.shop.disabled === open) v.shop.disabled = !open;
    v.shop.classList.toggle('open', open);
    put(v.shopText, salveWords(s, hr.i));
  }

  private castSkill(k: 0 | 1) {
    const h = this.sim.players[this.me]?.hero;
    if (!h || !this.sim.canCast(this.me, k)) return;
    if (!this.hooks.send({ t: 'cast', k })) this.say(`Nothing in reach for ${HERO[h.id].skills[k].name}`, 2);
  }
  /** Q and E (main.ts) cast the hero's skills. */
  /** Online, the sim turned down your command on its turn (it was fine when sent): say why, as offline would at once. */
  refused(c: Command) {
    const h = this.sim.players[this.me]?.hero;
    // only reach: a cast lost to its cooldown, mana or a stun since it was sent is already shown on its button (Flash final F1)
    if (c.t === 'cast' && h) { if (this.sim.canCast(this.me, c.k)) this.say(`Nothing in reach for ${HERO[h.id].skills[c.k].name}`, 2); }
    else if (c.t === 'buy') this.say(buyRefusal(this.sim, this.me, c.item), 2, 'warn');
  }
  key(k: 0 | 1) { if (this.me >= 0 && !this.sim.players[this.me].out) this.castSkill(k); }

  private buildHero(): HeroBox | undefined {
    const mine = this.sim.players[this.me].hero;
    if (!mine) return undefined;
    const d = HERO[mine.id], box = h('div', 'herobox');
    box.style.setProperty('--team', TEAM[this.me]);
    box.setAttribute('role', 'group'); box.setAttribute('aria-label', `Your hero, ${d.name}`);
    const info = h('div', 'hb-info plaque'), face = h('span', 'hb-face medal'), lv = h('b', 'hb-lv coin'), words = h('span', 'hb-words');
    const bars = h('span', 'hb-bars'), hpBar = h('span', 'hb-bar hp'), manaBar = h('span', 'hb-bar mana'), hp = h('i'), mana = h('i');
    const back = h('small', 'hb-back'), xpBox = h('span', 'fill'), xp = h('i');
    face.append(pic(heroPic(mine.id)), h('i', 'sweep'), lv); lv.title = 'Level';
    hpBar.append(hp); manaBar.append(mana); xpBox.append(xp); bars.append(hpBar, manaBar);
    hpBar.title = 'Health'; manaBar.title = 'Mana: fast at the center'; xpBox.title = 'To the next level';
    words.append(h('b', 'hb-name', d.short), bars, back);
    info.append(face, words, xpBox);
    box.append(info);
    const skills = d.skills.map((k, i): SkillBtn => {
      const b = h('button', 'skill plaque'), sweep = h('i', 'sweep'), secs = h('b', 'secs'), cost = h('small', 'cost'), costV = h('span');
      cost.append(sym('mana'), costV);
      b.append(pic(iconPic(k.name)), sweep, secs, h('kbd', 'key', i ? 'E' : 'Q'), cost);
      b.title = `${k.name} (${i ? 'E' : 'Q'}): ${friendly(k.text)}`;
      b.addEventListener('click', () => this.castSkill(i as 0 | 1));
      box.append(b);
      return { b, sweep, secs, cost, costV, state: '' };
    });
    const shop = h('button', 'shop plaque');
    const shopText = h('small', 'cost', 'Shop');
    shop.append(pic(iconPic('salve')), shopText);
    shop.setAttribute('aria-label', 'Shop at the center'); shop.title = 'Bring your hero to the center Shop';
    shop.addEventListener('click', () => this.openShop());
    box.append(shop);
    return { box, face, lv, hp, mana, xp, back, bars, skills, shop, shopText };
  }

  private openShop() {
    const s = this.sim, pl = s.players[this.me], hr = pl.hero;
    if (!hr) return;
    const grid = h('div', 'choices');
    for (const id of ITEMS) {
      const it = ITEM[id], owned = id === 'whetstone' ? hr.whet : 0, cost = it.cost(owned), b = h('button', 'plaque choice');
      const words = h('span', 'words'); words.append(h('b', '', it.name), h('small', 'unit', owned >= it.max ? 'All 5 bought' : `${cost} gold`), h('small', 'role', friendly(it.text)));
      b.append(pic(iconPic(id)), words);
      b.disabled = owned >= it.max || pl.gold < cost || (id === 'salve' && hr.i >= 0 && (s.hp[hr.i] >= s.maxHp[hr.i] || s.salveT[hr.i] > 0));
      b.addEventListener('click', () => {
        const ok = this.hooks.send({ t: 'buy', item: id });
        this.say(ok ? `Bought ${it.name}` : buyRefusal(s, this.me, id), 2, ok ? 'good' : 'warn');
        this.closeSheet();
      });
      grid.append(b);
    }
    this.openSheet('The center\'s shop', grid, `For ${HERO[hr.id].short}. You have ${Math.floor(pl.gold)} gold.`);
    this.sheetFor = 'shop';
  }

  /** Lift the hero above the tier button when the bar would run past the window, or squeeze a slot's words until they
   *  are cut (the slots shrink before the bar overflows: 1440x900 cut "Ranged", uiart 2026-10-26). Never on a wrapping
   *  portrait bar. */
  fitBar() {
    this.bar.classList.remove('lifted');
    if (getComputedStyle(this.bar).flexWrap !== 'nowrap') return;
    const cut = this.live.slotBtns.some((b) => b.scrollWidth > b.clientWidth + 1);
    if (cut || this.bar.scrollWidth > this.bar.clientWidth + 1) this.bar.classList.add('lifted');
  }

  private buildBar() {
    const s = this.sim, me = s.players[this.me], f = me.faction ? FACTION[me.faction] : null;
    const tier = h('button', 'tier plaque'), tierN = h('b', 'coin'), tierT = h('b', 'tier-t'), purse = h('small'), tierS = h('span'), gate = h('small', 'gate'), track = h('span', 'track'), tierFill = h('i');
    gate.innerHTML = LOCK_ICON; gate.append(h('span', '', mmss(TIER_GATE[me.tier] ?? 0))); gate.title = 'The next tier opens at this time';
    purse.append(sym('gold'), tierS, h('span', 'vh', ' gold'));
    track.append(tierFill); tier.append(tierN, tierT, gate, purse, track);
    tier.title = 'Buy a tier for new soldiers and another slot';
    tier.addEventListener('click', () => { if (me.tier === 0) this.hooks.pickArmy(); else this.hooks.send({ t: 'tier' }); });
    const slots = h('div', 'slots'); slots.setAttribute('role', 'group'); slots.setAttribute('aria-label', 'Spawn slots');
    const fills: HTMLElement[] = [], slotBtns: HTMLButtonElement[] = [];
    me.slots.forEach((c, i) => {
      const b = h('button', 'slot plaque'), bar = h('span', 'fill'), fi = h('i'), words = h('span', 'words');
      const unit = f?.names[c] ?? `One every ${Math.round(unitStats(c, me.tier, null).interval)} s`;
      words.append(h('b', '', CLASS_LABEL[c]), h('small', '', unit));
      b.append(pic(unitPic(c, me.faction)), words, bar); bar.append(fi);
      b.setAttribute('aria-label', `Slot ${i + 1}: ${CLASS_LABEL[c]}, ${unit}. Change`);
      if (me.tier === 0) { b.disabled = true; b.title = 'Pick an army to change soldiers'; }
      b.addEventListener('click', () => this.openSlot(i));
      fills.push(fi); slotBtns.push(b); slots.append(b);
    });
    const goals = h('div', 'goals'), goal = h('span', 'goal-now'), row = h('div', 'goal-btns');
    const mk = (label: string, icon: 'center' | 'defend' | 'attack', fn: () => void) => {
      const b = h('button', 'plaque goal'); b.append(sym(icon), h('span', '', label)); b.addEventListener('click', fn); row.append(b); return b;
    };
    const goalBtns = [
      mk('Center', 'center', () => this.setGoal({ kind: 'center' })),
      mk('Defend', 'defend', () => this.setGoal({ kind: 'base', p: this.me })),
      mk('Attack', 'attack', () => this.openAttack()),
    ];
    goals.append(goal, row);
    // the portrait phone's tab that folds the slot row away (hidden on other layouts)
    const fold = h('button', 'plaque fold');
    const hero = this.buildHero();
    // folded only with a hero row to fold into (the hero pick has none, and the tab would squeeze the tier plaque);
    // until the player taps the tab, a screen under 375 px (this.roomy, followed live) keeps the row open, since its
    // hero row has no room for the name beside the tab: folded, the name gets the width less 304 px, and at 360 that
    // cut Musashi, Leonidas, Wallace, Lagertha and Cuauhtemoc (2026-10-31, review F3). From 375 seven of the eight fit;
    // Cuauhtemoc needs 398, so below that .hb-name ends in an ellipsis (uishots 5_names checks every hero)
    const folded = () => !!hero && (this.folded ?? this.roomy.matches);
    const setFold = () => {
      this.bar.classList.toggle('folded', folded());
      fold.setAttribute('aria-expanded', String(!folded())); fold.setAttribute('aria-label', folded() ? 'Show your spawn slots' : 'Hide your spawn slots');
    };
    fold.innerHTML = FOLD_ICON; fold.append(h('span', '', 'Slots')); fold.setAttribute('aria-controls', 'hud-slots'); slots.id = 'hud-slots';
    fold.addEventListener('click', () => { this.folded = !folded(); setFold(); });
    setFold(); this.applyFold = setFold;
    this.bar.replaceChildren(tier, ...(hero ? [hero.box] : []), fold, slots, goals, this.groups);
    this.bar.dataset.slots = String(me.slots.length);
    this.live = { tier, tierT, tierN, tierS, tierFill, fills, slotBtns, goal, goalBtns, hero };
    this.fitBar();
    // a tier-up while a slot sheet is open: show the new choices, not stale ones
    if (typeof this.sheetFor === 'number') this.openSlot(this.sheetFor);
  }

  private setGoal(goal: Goal) { if (!this.hooks.goal?.(goal)) { this.hooks.send({ t: 'goal', goal }); this.flashGoal(goal); } this.closeSheet(); }
  /** A card over the bar for another part of the HUD (a control group's, control/controls.ts). */
  panel(title: string, body: HTMLElement, note = '') { this.openSheet(title, body, note); this.sheetFor = 'none'; }

  private openSheet(title: string, body: HTMLElement, note: string | HTMLElement = '') {
    const close = h('button', 'plaque close', 'Close');
    close.addEventListener('click', () => this.closeSheet());
    const head = h('div', 'sheet-head'); head.append(h('b', '', title), close);
    this.sheet.replaceChildren(head, ...(note ? [typeof note === 'string' ? h('p', 'note', note) : note] : []), body);
    this.sheet.hidden = false;
    this.slotSheet = null; this.extraBtn = null;
  }
  closeSheet() { this.sheet.hidden = true; this.sheetFor = 'none'; this.slotSheet = null; this.extraBtn = null; }
  sheetOpen() { return !this.sheet.hidden; }

  private openSlot(i: number) {
    const s = this.sim, pl = s.players[this.me], f = pl.faction ? FACTION[pl.faction] : null;
    if (i >= pl.slots.length) { this.closeSheet(); return; }
    const grid = h('div', 'choices'), locked = pl.slotLock[i] > s.time, btns: { b: HTMLButtonElement; full: boolean }[] = [];
    for (const c of slotChoices(pl.tier)) {
      const b = h('button', 'plaque choice'), words = h('span', 'words');
      words.append(h('b', '', CLASS_LABEL[c]), h('small', 'unit', f?.names[c] ?? ''), h('small', 'role', CLASS_ROLE[c]));
      b.append(pic(unitPic(c, pl.faction)), words);
      const full = pl.slots[i] !== c && pl.slots.filter((x, k) => k !== i && x === c).length >= CLASS[c].maxSlots;
      b.setAttribute('aria-pressed', String(pl.slots[i] === c));
      b.disabled = locked || full;
      if (full) b.title = `You already have the most ${CLASS_LABEL[c]} slots`;
      b.addEventListener('click', () => { this.hooks.send({ t: 'slot', i, cls: c }); this.closeSheet(); });
      grid.append(b); btns.push({ b, full });
    }
    const note = h('p', 'note', locked ? `Change again in ${Math.ceil(pl.slotLock[i] - s.time)} s.` : '');
    note.hidden = !locked;
    this.openSheet(`Slot ${i + 1} sends`, grid, note);
    this.sheetFor = i; this.slotSheet = { i, note, btns };
  }

  /** The Extra's button as things stand: shut with the reason (the tier, its wait, the gold, a full army), else its price. */
  private updateExtra() {
    const x = this.extraBtn!, s = this.sim, mine = s.players[this.me], wait = Math.ceil(mine.extraAt - s.time);
    const why = mine.tier < EXTRA.tier ? `Opens at tier ${EXTRA.tier}` : wait > 0 ? `Ready in ${wait} s` : mine.gold < EXTRA.cost ? 'Not enough gold'
      : mine.alive >= ECON.cap ? 'Your army is full' : '';
    put(x.unit, why || `${EXTRA.cost} gold`);
    const off = !s.canExtra(this.me);
    if (x.b.disabled !== off) x.b.disabled = off;
    const label = `Send a ${x.name}, ${EXTRA.cost} gold. ${CLASS_ROLE.ram}.${why ? ` ${why}.` : ''}`;
    if (x.b.getAttribute('aria-label') !== label) x.b.setAttribute('aria-label', label);
  }

  private openAttack() {
    const s = this.sim, grid = h('div', 'choices foes');
    s.players.forEach((pl, p) => {
      if (p === this.me || pl.out) return;
      const b = h('button', 'plaque choice foe'), words = h('span', 'words'); b.style.setProperty('--team', TEAM[p]);
      words.append(h('b', '', TEAM_NAME[p]), h('small', 'unit', pl.faction ? FACTION[pl.faction].name : 'Footmen'), h('small', 'role', `Base ${Math.round((pl.baseHp / pl.baseMax) * 100)}%, ${pl.alive} soldiers`));
      b.append(pic(pl.faction ? armyPic(pl.faction) : pl.hero ? heroPic(pl.hero.id) : { strip: 'none', frame: 0 }), words);
      b.addEventListener('click', () => this.setGoal({ kind: 'base', p }));
      grid.append(b);
    });
    this.openSheet('Attack which base?', grid, 'Tap a base to attack. Tap ground to rally.');
    // the army's Extra, a ram to break the wall you march on (EXTRA in defs.ts), beside Close so the sheet keeps its
    // height (a choice under the bases ran off a landscape phone, uishots 844x390)
    const mine = s.players[this.me], f = mine.faction ? FACTION[mine.faction] : null, ram = f?.names.ram;
    if (f?.ram && ram) {
      const b = h('button', 'plaque choice extra'), words = h('span', 'words'), unit = h('small', 'unit');
      words.append(h('b', '', ram), unit);
      b.append(pic(unitPic('ram', mine.faction)), words);
      b.title = `Send ${ram}: ${EXTRA.cost} gold. Break enemy walls.`;
      b.addEventListener('click', () => { if (this.hooks.send({ t: 'extra' })) this.say(`A ${ram} is out`, 2, 'good'); this.closeSheet(); });
      const head = this.sheet.querySelector('.sheet-head')!;
      head.insertBefore(b, head.lastElementChild);
      this.extraBtn = { b, unit, name: ram };
      this.updateExtra();
    }
    this.sheetFor = 'attack';
  }
}
